import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductsDropdownOpen, setIsProductsDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'glass-panel shadow-lg'
          : 'bg-transparent'
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-24">
          {/* Logo - Bigger Size */}
          <button
            onClick={() => scrollToSection('home')}
            className="flex items-center gap-3"
            data-testid="nav-logo"
          >
            <img 
              src="/agatsya-logo.png" 
              alt="Agatsya International" 
              className="h-16 w-auto md:h-20"
            />
          </button>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('home')}
              className="nav-link"
              data-testid="nav-home"
            >
              Home
            </button>
            
            {/* Products Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsProductsDropdownOpen(true)}
              onMouseLeave={() => setIsProductsDropdownOpen(false)}
            >
              <button
                className="nav-link flex items-center gap-1"
                data-testid="nav-products"
              >
                Products
                <ChevronDown size={16} className={`transition-transform ${isProductsDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isProductsDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 glass-panel rounded-lg shadow-xl border border-gold/20 py-2 animate-fade-in">
                  <button
                    onClick={() => {
                      scrollToSection('export');
                      setIsProductsDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 text-silver hover:text-gold hover:bg-gold/10 transition-colors"
                    data-testid="nav-agro-exports"
                  >
                    <span className="font-semibold">Agro Exports</span>
                    <p className="text-xs text-gray-400 mt-1">Turmeric, Chilli, Jaggery</p>
                  </button>
                  <button
                    onClick={() => {
                      scrollToSection('chemicals');
                      setIsProductsDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 text-silver hover:text-gold hover:bg-gold/10 transition-colors"
                    data-testid="nav-chemicals"
                  >
                    <span className="font-semibold">Chemical Division</span>
                    <p className="text-xs text-gray-400 mt-1">Industrial & Cleaning Solutions</p>
                  </button>
                </div>
              )}
            </div>
            
            <button
              onClick={() => scrollToSection('financial')}
              className="nav-link"
              data-testid="nav-financial"
            >
              Financial Services
            </button>
            <button
              onClick={() => scrollToSection('consultancy')}
              className="nav-link"
              data-testid="nav-consultancy"
            >
              Consultancy
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="nav-link"
              data-testid="nav-about"
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="btn-gold px-6 py-3 text-sm"
              data-testid="nav-contact-btn"
            >
              Contact Us
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-gold p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            className="lg:hidden glass-panel mt-2 rounded-lg py-4 animate-fade-in"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col space-y-4 px-6">
              <button
                onClick={() => scrollToSection('home')}
                className="text-silver hover:text-gold text-left py-2 transition-colors"
                data-testid="mobile-nav-home"
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection('export')}
                className="text-silver hover:text-gold text-left py-2 transition-colors"
                data-testid="mobile-nav-export"
              >
                Agro Exports
              </button>
              <button
                onClick={() => scrollToSection('chemicals')}
                className="text-silver hover:text-gold text-left py-2 transition-colors"
                data-testid="mobile-nav-chemicals"
              >
                Chemical Division
              </button>
              <button
                onClick={() => scrollToSection('financial')}
                className="text-silver hover:text-gold text-left py-2 transition-colors"
                data-testid="mobile-nav-financial"
              >
                Financial Services
              </button>
              <button
                onClick={() => scrollToSection('consultancy')}
                className="text-silver hover:text-gold text-left py-2 transition-colors"
                data-testid="mobile-nav-consultancy"
              >
                Consultancy
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="text-silver hover:text-gold text-left py-2 transition-colors"
                data-testid="mobile-nav-about"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="btn-gold w-full text-center mt-4"
                data-testid="mobile-nav-contact-btn"
              >
                Contact Us
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
